// services/ProfileService.js - ReactJS (web)
import api from '../api/api';

class ProfileService {
  // ==================== PROFILE MANAGEMENT ====================

  /**
   * Get user profile
   * GET /api/profile
   */
  async getProfile() {
    try {
      const response = await api.get('/profile');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update profile info (name, etc.)
   * PUT /api/profile/info
   */
  async updateProfileInfo(data) {
    try {
      const response = await api.put('/profile/info', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PROFILE IMAGE ====================

  /**
   * Update profile image
   * POST /api/profile/image
   */
  async updateProfileImage(imageFile) {
    try {
      const formData = new FormData();
      
      // For web, we need to handle File objects differently
      if (imageFile instanceof File) {
        // Browser File object
        formData.append('image', imageFile);
      } else if (imageFile.uri) {
        // React Native style object - convert for web
        // In web, we typically get File objects from input[type="file"]
        // This handles the case where the component passes a React Native style object
        const response = await fetch(imageFile.uri);
        const blob = await response.blob();
        formData.append('image', blob, imageFile.name || 'profile.jpg');
      } else {
        // Assume it's already a Blob or File
        formData.append('image', imageFile);
      }

      const response = await api.post('/profile/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove profile image
   * DELETE /api/profile/image
   */
  async removeProfileImage() {
    try {
      const response = await api.delete('/profile/image');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PUBLIC PROFILES ====================

  /**
   * Get public profile (for viewing by others)
   * GET /api/profile/public/:userId
   */
  async getPublicProfile(userId) {
    try {
      const response = await api.get(`/profile/public/${userId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user by email (public)
   * GET /api/profile/public/email/:email
   */
  async getPublicUserByEmail(email) {
    try {
      const response = await api.get(`/profile/public/email/${email}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== SETTINGS ====================

  /**
   * Get user settings
   * GET /api/profile/settings
   */
  async getUserSettings() {
    try {
      const response = await api.get('/profile/settings');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update user settings
   * PUT /api/profile/settings
   */
  async updateSettings(settings) {
    try {
      const response = await api.put('/profile/settings', settings);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update notification preferences
   * PUT /api/profile/notifications
   */
  async updateNotificationPreferences(preferences) {
    try {
      const response = await api.put('/profile/notifications', preferences);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ACTIVITY ====================

  /**
   * Get user activity log (last login, last active)
   * GET /api/profile/activity
   */
  async getUserActivity() {
    try {
      const response = await api.get('/profile/activity');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update last active timestamp
   * POST /api/profile/activity
   */
  async updateUserActivity() {
    try {
      const response = await api.post('/profile/activity');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PROFILE COMPLETION ====================

  /**
   * Get profile completion status
   * GET /api/profile/completion
   */
  async getProfileCompletion() {
    try {
      const response = await api.get('/profile/completion');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== LANGUAGE ====================

  /**
   * Update user language preference
   * PUT /api/profile/language
   */
  async updateLanguage(language) {
    try {
      const response = await api.put('/profile/language', { language });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ACCOUNT MANAGEMENT ====================

  /**
   * Delete user account (soft delete)
   * DELETE /api/profile/account
   */
  async deleteAccount() {
    try {
      const response = await api.delete('/profile/account');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reactivate deleted account
   * POST /api/profile/account/reactivate
   */
  async reactivateAccount() {
    try {
      const response = await api.post('/profile/account/reactivate');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Validate image file
   */
  validateImage(file) {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!file) {
      return { valid: false, error: 'No image file provided' };
    }

    // Check file size
    if (file.size && file.size > maxSize) {
      return { valid: false, error: 'Image size must be less than 5MB' };
    }

    // Check file type
    if (file.type && !allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Image type not supported. Please use JPEG, PNG, GIF, or WebP' };
    }

    return { valid: true };
  }

  /**
   * Get default avatar URL
   */
  getDefaultAvatar(name) {
    if (!name) return null;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=200`;
  }

  /**
   * Format profile data for display
   */
  formatProfile(profile) {
    if (!profile) return null;

    return {
      ...profile,
      displayName: profile.name || 'User',
      avatarUrl: profile.profileImage?.url || this.getDefaultAvatar(profile.name),
      isProfileComplete: this.checkProfileCompletion(profile),
      joinedDate: profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : null,
    };
  }

  /**
   * Check if profile is complete
   */
  checkProfileCompletion(profile) {
    if (!profile) return false;

    const requiredFields = [
      'name',
      'email',
      'phone',
      'profileImage'
    ];

    return requiredFields.every(field => {
      if (field === 'profileImage') {
        return profile.profileImage?.url;
      }
      return !!profile[field];
    });
  }

  /**
   * Mask email for privacy
   */
  maskEmail(email) {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (local.length <= 2) return email;
    return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
  }

  /**
   * Mask phone number
   */
  maskPhone(phone) {
    if (!phone) return '';
    const visible = phone.length > 4 ? phone.slice(-4) : phone;
    return `****${visible}`;
  }

  /**
   * Create FormData from File object (web helper)
   */
  createFormData(file) {
    const formData = new FormData();
    formData.append('image', file);
    return formData;
  }

  /**
   * Get image preview URL (web helper)
   */
  getImagePreviewUrl(file) {
    if (!file) return null;
    return URL.createObjectURL(file);
  }

  /**
   * Revoke image preview URL (web helper)
   */
  revokeImagePreviewUrl(url) {
    if (url) {
      URL.revokeObjectURL(url);
    }
  }
}

export default new ProfileService();